import React, { useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import {FaDatabase, FaCode, FaShieldAlt, FaSyncAlt,FaProjectDiagram,FaUsersCog, FaBolt, FaWhatsapp, FaEnvelope, FaPhoneAlt, FaRobot, FaLayerGroup, FaChevronDown, FaStore, FaTruck, FaGlobe,FaTools
} from "react-icons/fa";
import AnimationRevealPage from "../helpers/AnimationRevealPage";
import PageHeader from "../Components/headers/PageHeader";

// --- 1. LAYOUT ARCHITECTURE (STRICT CONSTANTS) ---

const PageContainer = styled.div`
  ${tw`bg-white font-sans text-secondary-900 antialiased w-full`}
`;

const SectionFrame = styled.section`
  ${tw`relative w-full overflow-hidden border-b border-gray-200`}
`;

const SplitGrid = styled.div`
  ${tw`grid grid-cols-1 lg:grid-cols-12 w-full`}
`;

const MainPanel = styled.div`
  ${tw`lg:col-span-7 p-8 md:p-16 flex flex-col justify-center`}
`;

const SidePanel = styled.div`
  ${tw`lg:col-span-5 bg-gray-50 p-8 md:p-24 flex flex-col justify-center border-l border-gray-200`}
`;

const DarkMainPanel = styled(MainPanel)`
  ${tw`bg-secondary-900 text-white border-none`}
`;

const DarkSidePanel = styled(SidePanel)`
  ${tw`bg-secondary-800 text-white border-none`}
`;

// --- 2. TYPOGRAPHY ---

const HeroTitle = styled.h1`
  ${tw`text-5xl md:text-7xl font-black tracking-tighter leading-none mb-10 text-secondary-900 uppercase`}
`;

const SectionHeading = styled.h2`
  ${tw`text-4xl md:text-6xl font-black tracking-tighter leading-none mb-4 uppercase`}
`;

const WhiteHeading = styled(SectionHeading)`
  ${tw`text-white`}
`;

const Label = styled.span`
  ${tw`block text-[#ff5464] font-black tracking-[0.4em] uppercase text-xs mb-6`}
`;

const WhiteLabel = styled(Label)`
  ${tw`text-white opacity-60`}
`;

const LeadText = styled.p`
  ${tw`text-xl md:text-2xl text-secondary-600 leading-relaxed font-light mb-10 max-w-3xl`}
`;

const WhiteLeadText = styled(LeadText)`
  ${tw`text-white opacity-80 mx-auto text-center`}
`;

const Paragraph = styled.p`
  ${tw`text-lg text-secondary-500 leading-relaxed mb-6 max-w-2xl`}
`;

const WhiteParagraph = styled(Paragraph)`
  ${tw`text-gray-400`}
`;

const Red = styled.span`
  ${tw`text-[#ff5464]`}
`;

// --- 3. COMPONENT MODULES ---

const TechGrid = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-2 gap-px bg-white mt-12 rounded-xl border border-gray-200`}
`;

const TechBox = styled.div`
  ${tw`bg-white p-10 hover:bg-gray-200 rounded-lg transition-colors duration-300`}
`;

const BoxIcon = styled.div`
  ${tw`text-[#ff5464] text-3xl mb-6`}
`;

const BoxTitle = styled.h3`
  ${tw`text-xl font-black text-black mb-4 uppercase tracking-tight`}
`;

const BoxDesc = styled.p`
  ${tw`text-sm text-secondary-500 leading-relaxed`}
`;

const SpecList = styled.div`
  ${tw`space-y-12 mt-12 border border-gray-700 rounded-xl p-6`}
`;

const SpecItem = styled.div`
  ${tw`flex flex-col md:flex-row gap-6 md:items-start border-t border-gray-200 pt-8`}
`;

const SpecLabel = styled.div`
  ${tw`md:w-1/3 flex items-center gap-3 text-[#ff5464] text-sm font-black uppercase tracking-widest`}
`;

const SpecValue = styled.div`
  ${tw`md:w-2/3 text-lg font-bold text-secondary-900 text-start`}
`;

const WhiteSpecItem = styled(SpecItem)`
  ${tw`border-white/10`}
`;

const WhiteSpecValue = styled(SpecValue)`
  ${tw`text-white text-start`}
`;

// --- 4. FAQ ---

const FAQBlock = styled.div`
  ${tw`mt-20 max-w-6xl mx-auto`}
`;

const FAQRow = styled.div`
  ${tw`border-b border-gray-200 mb-4`}
`;

const FAQHeader = styled.button`
  ${tw`flex justify-between px-6 rounded-xl items-center w-full py-6 text-left text-xl tracking-tighter hover:text-[#ff5464] transition-all`}
`;

const FAQBody = styled.div`
  ${tw`overflow-hidden transition-all mt-4 duration-500 text-lg text-secondary-500 leading-relaxed`}
  max-height: ${props => (props.isOpen ? "400px" : "0")};
  padding-bottom: ${props => (props.isOpen ? "1rem" : "0")};
  opacity: ${props => (props.isOpen ? "1" : "0")};
`;

const ChevronIcon = styled(FaChevronDown)`
  ${tw`text-sm transition-transform duration-500`}
  transform: ${props => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
`;

// --- 5. CALL TO ACTION ---

const CTAContainer = styled.div`
  ${tw`bg-[#ff5464] w-full py-12 text-white text-center`}
`;

const CTALinksGrid = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-3 gap-2 px-6 mt-20 border border-white/20`}
`;

const CTAAnchor = styled.a`
  ${tw`bg-[#ff5464] p-12 flex flex-col rounded-xl items-center justify-center gap-4 hover:bg-white hover:rounded-xl hover:text-[#ff5464] transition-all duration-500 no-underline text-white`}
`;

const CTAIconWrapper = styled.div`
  ${tw`text-4xl mb-2`}
`;

const CTALabel = styled.span`
  ${tw`text-lg font-black uppercase tracking-widest`}
`;

const LegalBranding = styled.p`
  ${tw`mt-20 text-[10px] font-black uppercase tracking-[0.6em] opacity-50`}
`;

// --- MAIN COMPONENT ---

export const OdooImplementationService = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    { 
      q: "Why should I hire Odoo Developers in Dubai specifically?", 
      a: "Actually, local developers understand the UAE business landscape—from VAT accounting rules to the importance of mobile-first commerce for the Dubai market. At Milaaj, we provide on-the-ground support that offshore teams simply cannot match." 
    },
    { 
      q: "Is Odoo suitable for a Retail POS setup in Dubai?", 
      a: "Indeed! Odoo Retail POS is one of our most requested services. It allows you to sync in-store sales with your online inventory and accounting in real-time, giving you a 360-degree view of your business." 
    },
    { 
      q: "Can you integrate Odoo with my existing Shopify or Magento store?", 
      a: "Specifically, yes. Our Odoo Integration Services include custom API connectors for all major e-commerce platforms, ensuring your orders, stock, and customers stay in perfect sync across all channels." 
    },
    { 
      q: "What is the difference between Odoo Implementation and Odoo Customisation?", 
      a: "Ideally, implementation is the setup of existing modules, while Odoo Customisation involves writing new code to tailor the software to a specific workflow that Odoo doesn't offer 'out of the box'." 
    }
  ];

  return (
    <AnimationRevealPage>
      <PageHeader from="Odoo Technical Bureau" />
      <PageContainer>
        
        {/* --- SECTION 1: HERO & CORE INTENT --- */}
        <SectionFrame>
          <SplitGrid>
            <MainPanel>
              <Label>Bureau of Digital Architecture</Label>
              <HeroTitle>
                Custom Odoo <Red>Development</Red>.
              </HeroTitle>
              <LeadText>
                At Milaaj mobile and laptop repair Dubai (and our brandset division), we don't just "install" software; we build it. 
                We craft Odoo Software Development solutions that speak your unique business language.
              </LeadText>
            </MainPanel>
            <SidePanel>
              <BoxIcon><FaProjectDiagram size={50} /></BoxIcon>
              <BoxTitle>Beyond Implementation</BoxTitle>
              <Paragraph>
                Whether you need an Odoo Addon Development for a logistical hurdle or a completely Custom Module, 
                our Odoo Developers in Dubai craft code that follows official standards while solving local complexities.
              </Paragraph>
            </SidePanel>
          </SplitGrid>
        </SectionFrame>

        {/* --- SECTION 2: EXPERT DEVELOPMENT SERVICES --- */}
        <SectionFrame>
          <SplitGrid>
            <DarkSidePanel>
               <WhiteHeading>The Future <br/> is Here: <br/> Headless & AI.</WhiteHeading>
               <WhiteParagraph>
                 In 2026, the "all-in-one" look isn't always enough. Progressive Dubai firms are moving toward Odoo Headless Development 
                 to decouple powerful backends from ultra-fast, custom frontends.
               </WhiteParagraph>
            </DarkSidePanel>
            <DarkMainPanel>
              <TechGrid>
                <TechBox>
                  <BoxIcon><FaRobot /></BoxIcon>
                  <BoxTitle>Gen-AI Ready</BoxTitle>
                  <BoxDesc>Bridge the gap with Gen-AI integration, enabling predictive inventory and real-time AI chatbots for 24/7 assistance.</BoxDesc>
                </TechBox>
                <TechBox>
                  <BoxIcon><FaLayerGroup /></BoxIcon>
                  <BoxTitle>Headless Odoo</BoxTitle>
                  <BoxDesc>Build lightning-fast frontends using React or Vue while Odoo handles Inventory, Accounting, and CRM.</BoxDesc>
                </TechBox>
                <TechBox>
                  <BoxIcon><FaStore /></BoxIcon>
                  <BoxTitle>Retail POS</BoxTitle>
                  <BoxDesc>Specialized Odoo for Retail POS and Grocery chains with real-time omnichannel synchronization.</BoxDesc>
                </TechBox>
                <TechBox>
                  <BoxIcon><FaTruck /></BoxIcon>
                  <BoxTitle>Wholesale</BoxTitle>
                  <BoxDesc>Advanced Wholesale Distribution modules with landed cost intelligence and automated procurement.</BoxDesc>
                </TechBox>
              </TechGrid>
            </DarkMainPanel>
          </SplitGrid>
        </SectionFrame>

        {/* --- SECTION 3: INTEGRATION & MIGRATION --- */}
        <SectionFrame>
          <SplitGrid>
            <MainPanel>
              <Label>Integration Ecosystem</Label>
              <SectionHeading>Seamless Migration <br/> & API Connectivity.</SectionHeading>
              <LeadText>
                Moving your data shouldn't feel like a root canal. Our <Red>Odoo Migration Services</Red> ensure a 
                smooth transition from legacy systems with zero data loss.
              </LeadText>
              <TechGrid>
                <TechBox>
                  <BoxIcon><FaSyncAlt /></BoxIcon>
                  <BoxTitle>Third-Party Connectors</BoxTitle>
                  <BoxDesc>Connect to local payment gateways like Telr or Checkout.com and shipping providers like Aramex.</BoxDesc>
                </TechBox>
                <TechBox>
                  <BoxIcon><FaDatabase /></BoxIcon>
                  <BoxTitle>E-commerce Sync</BoxTitle>
                  <BoxDesc>Custom API connectors for Shopify, Magento, and WooCommerce for automated stock and order flows.</BoxDesc>
                </TechBox>
              </TechGrid>
            </MainPanel>
            <SidePanel>
               <Label>High-Intent Specs</Label>
               <SpecList>
                 <SpecItem>
                   <SpecLabel><FaCode /> Custom</SpecLabel>
                   <SpecValue>Bespoke Addon Development</SpecValue>
                 </SpecItem>
                 <SpecItem>
                   <SpecLabel><FaGlobe /> Local</SpecLabel>
                   <SpecValue>UAE Govt. Portal Integrations</SpecValue>
                 </SpecItem>
                 <SpecItem>
                   <SpecLabel><FaBolt /> Trends</SpecLabel>
                   <SpecValue>Smarter AI Integrations</SpecValue>
                 </SpecItem>
               </SpecList>
            </SidePanel>
          </SplitGrid>
        </SectionFrame>

        {/* --- SECTION 4: THE MILAAJ ADVANTAGE --- */}
        <SectionFrame>
          <SplitGrid>
            <DarkSidePanel>
               <WhiteLabel>Why Choose Us</WhiteLabel>
               <WhiteHeading>The Milaaj <br/> Advantage.</WhiteHeading>
               <WhiteParagraph>
                 As a top Odoo ERP Company in Dubai, we deliver localized expertise without the "enterprise-only" price tag.
               </WhiteParagraph>
            </DarkSidePanel>
            <DarkMainPanel>
              <SpecList>
                <WhiteSpecItem>
                  <SpecLabel><FaShieldAlt /> Affordable</SpecLabel>
                  <WhiteSpecValue>Flexible pricing models tailored for SMEs and growing Dubai startups.</WhiteSpecValue>
                </WhiteSpecItem>
                <WhiteSpecItem>
                  <SpecLabel><FaBolt /> Speed</SpecLabel>
                  <WhiteSpecValue>"Quick Win" implementation focus to ensure ROI in weeks, not months.</WhiteSpecValue>
                </WhiteSpecItem>
                <WhiteSpecItem>
                  <SpecLabel><FaUsersCog /> Local Compliance</SpecLabel>
                  <WhiteSpecValue>VAT compliant, Arabic/English bilingual interfaces, and UAE labor law support.</WhiteSpecValue>
                </WhiteSpecItem>
                <WhiteSpecItem>
                  <SpecLabel><FaTools /> Support</SpecLabel>
                  <WhiteSpecValue>Dedicated Odoo AMC Services and staff training to ensure long-term evolution.</WhiteSpecValue>
                </WhiteSpecItem>
              </SpecList>
            </DarkMainPanel>
          </SplitGrid>
        </SectionFrame>

        {/* --- SECTION 6: OPTIMIZED FAQ --- */}
        <SectionFrame>
          <MainPanel>
            <Label>Knowledge Base</Label>
            <SectionHeading>Technical FAQ.</SectionHeading>
            <FAQBlock>
              {faqs.map((f, i) => (
                <FAQRow key={i}>
                  <FAQHeader onClick={() => setOpenIndex(openIndex === i ? null : i)}>
                    {f.q}
                    <ChevronIcon isOpen={openIndex === i} />
                  </FAQHeader>
                  <FAQBody isOpen={openIndex === i}>
                    {f.a}
                  </FAQBody>
                </FAQRow>
              ))}
            </FAQBlock>
          </MainPanel>
        </SectionFrame>

        {/* --- SECTION 7: CTA --- */}
        <CTAContainer>
          <WhiteHeading>Build Your Custom <br/> Odoo Ecosystem.</WhiteHeading>
          <WhiteLeadText>
            Consult with our Odoo Developers in Dubai today for a specialized roadmap.
          </WhiteLeadText>
          
          <CTALinksGrid>
            <CTAAnchor href="tel:+971552633884">
              <CTAIconWrapper><FaPhoneAlt /></CTAIconWrapper>
              <CTALabel>+971 55 263 3884</CTALabel>
            </CTAAnchor>
            <CTAAnchor href="https://wa.me/+971552633884" target="_blank">
              <CTAIconWrapper><FaWhatsapp /></CTAIconWrapper>
              <CTALabel>WhatsApp Experts</CTALabel>
            </CTAAnchor>
            <CTAAnchor href="mailto:support@milaajbrandset.com">
              <CTAIconWrapper><FaEnvelope /></CTAIconWrapper>
              <CTALabel>Email Brief</CTALabel>
            </CTAAnchor>
          </CTALinksGrid>

          <LegalBranding>
            Dubai, United Arab Emirates • Milaaj Brandset Division
          </LegalBranding>
        </CTAContainer>

      </PageContainer>
    </AnimationRevealPage>
  );
};

export default OdooImplementationService;